import { getFormErrorMessage } from "@core/utility/get-error-message";
import { PackageFormRule, PackageFormSchema } from "@factory/form/form.rule";
import { useFormDefaultPackage } from "@factory/form/form-default-package.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponsePackageModel } from "@factory/model/query-model-package";
import { 
  useQyDeletePackage, 
  useQyGetPackageById, 
  useQyUpdatePackage} from "@factory/query/package.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditPackage() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultPackage();
  const navigate = useNavigate();
  const { mapUpdatePackage, mapDeletePackage } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { packageId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Package
  const handleGetApiSuccess = (data: GetResponsePackageModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("name", responseData?.name || "");
      setValue("numPeople", responseData?.num_people);
      setValue("unit", responseData?.unit || "");
      setValue("sizeDescription", responseData?.size_description || "");
  };
  const {
    data: packageResponse,
    isLoading,
    isError: packageError,
  } = useQyGetPackageById(packageId || "", handleGetApiSuccess);
  const packageData = packageResponse?.data?.[0];

  // API UPDATE Package
  const handleUpdateApiSuccess = () => {
    showSuccess("Package updated successfully");
  };
  const { mutate: updatePackage, isLoading: isEditLoading } = useQyUpdatePackage(
    handleUpdateApiSuccess
  );

  // API DELETE Package
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Package deleted successfully");
  };
  const { mutate: deletePackage } = useQyDeletePackage(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<PackageFormSchema>({
    defaultValues: getDefault(packageData),
    resolver: zodResolver(PackageFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: PackageFormSchema) => {
    if (!packageData || !packageData.code) {
      throw new Error("No package data");
    }

    const formData = mapUpdatePackage(form, packageData.code);
    updatePackage(formData);
  };
  const handleValidateError = (err: FieldErrors<PackageFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!packageData || !packageData.code) {
      throw new Error("No package data found");
    }

    const form = mapDeletePackage(packageData.code);
    deletePackage(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    packageId,
    packageData,
    isLoading,
    packageError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}